import React from "react";
import Headerdash from "./head";
import Sidebar from "./sidebar";
import { useNavigate } from "react-router-dom";
import { BookOpenCheck, Cpu, LayoutGrid } from "lucide-react";

const practiceSections = [
  {
    title: "Speaking Zone",
    color: "bg-green-300",
    hover: "hover:bg-green-500",
    accent: "bg-green-300",
    tint: "bg-green-50",
    text: "text-green-700",
    buttons: [
      { label: "Read Aloud", path: "/practice/readaloud" },
      { label: "Repeat Sentence", path: "/practice/repeatsentence" },
      { label: "Describe Image", path: "/practice/describeimage" },
      { label: "Re-Tell Lecture", path: "/practice/re-tell-lectures" },
      { label: "Short Questions", path: "/practice/shortquestions" },
      {
        label: "Summarize Group Discussion",
        path: "/practice/summarizegroupdiscussion",
      },
      {
        label: "Respond to a Situation",
        path: "/practice/respondtosituation",
      },
    ],
  },
  {
    title: "Writing Zone",
    color: "bg-blue-300",
    hover: "hover:bg-blue-500",
    accent: "bg-blue-300",
    tint: "bg-blue-50",
    text: "text-blue-700",
    buttons: [
      {
        label: "Summarize written text",
        path: "/practice/summarize-written-text",
      },
      { label: "Write Essay", path: "/practice/write-essay" },
    ],
  },
  {
    title: "Reading Zone",
    color: "bg-orange-300",
    hover: "hover:bg-orange-500",
    accent: "bg-orange-300",
    tint: "bg-orange-50",
    text: "text-orange-700",
    buttons: [
      {
        label: "Multiple Type, Single Answer",
        path: "/practice/reading-zone/single-answer",
      },
      {
        label: "Multiple Type, Double Answer",
        path: "/practice/reading-zone/double-answer",
      },
      {
        label: "Reorder Paragraph",
        path: "/practice/reading-zone/re-order-paragraph",
      },
      {
        label: "Reading Fill in the Blanks",
        path: "/practice/reading-zone/fill-in-the-blanks",
      },
      {
        label: "Fill in the Blanks Reading & Writing",
        path: "/practice/reading-zone/reading-and-writing",
      },
    ],
  },
  {
    title: "Listening Zone",
    color: "bg-purple-400",
    hover: "hover:bg-purple-600",
    accent: "bg-purple-400",
    tint: "bg-purple-50",
    text: "text-purple-700",
    buttons: [
      {
        label: "Summarize Spoken text",
        path: "/practice/listening-zone/summarize-spoken-text",
      },
      {
        label: "MCQ Single Answer",
        path: "/practice/listening-zone/mcq-single-answer",
      },
      {
        label: "MCQ Multiple Answer",
        path: "/practice/listening-zone/mcq-double-answer",
      },
      {
        label: "Fill In the Blanks",
        path: "/practice/listening-zone/fill-blanks",
      },
      {
        label: "Highlight Correct Summary",
        path: "/practice/listening-zone/highlight-correct-summary",
      },
      {
        label: "Select Missing Word",
        path: "/practice/listening-zone/select-missing-words",
      },
      {
        label: "Highlight Incorrect Word",
        path: "/practice/listening-zone/hightlight-incorrect-words",
      },
      {
        label: "Write from Dictation",
        path: "/practice/listening-zone/write-from-dictation",
      },
    ],
  },
];

const totalPracticeTypes = practiceSections.reduce(
  (total, section) => total + section.buttons.length,
  0
);

export default function Practice() {
  const navigate = useNavigate();

  const handleClick = (destination) => {
    navigate(destination);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-gray-700">
      <Headerdash />
      <Sidebar />

      <main className="min-h-screen pt-24 sm:pl-72">
        <div className="mx-auto w-full max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
          <section className="mb-6 rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Practice Material
                </p>
                <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
                  Choose your practice zone
                </h1>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center">
                <div className="flex items-center gap-3 rounded-md bg-slate-50 px-4 py-3">
                  <span className="grid h-10 w-10 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
                    <LayoutGrid className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500">
                      Zones
                    </p>
                    <p className="text-xl font-extrabold text-gray-900">
                      {practiceSections.length}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 rounded-md bg-slate-50 px-4 py-3">
                  <span className="grid h-10 w-10 place-items-center rounded-md bg-white text-purple-700 shadow-sm">
                    <BookOpenCheck className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500">
                      Types
                    </p>
                    <p className="text-xl font-extrabold text-gray-900">
                      {totalPracticeTypes}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="space-y-5">
            {practiceSections.map((section) => (
              <section
                key={section.title}
                className="w-full overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm transition hover:border-purple-100 hover:shadow-md"
              >
                <div className={`h-1.5 w-full ${section.accent}`} />

                <div className="p-4 sm:p-5">
                  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        className={`grid h-11 w-11 shrink-0 place-items-center rounded-md ${section.tint} ${section.text}`}
                      >
                        <Cpu className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <h2 className="min-w-0 truncate text-2xl font-bold text-gray-800 sm:text-3xl">
                        {section.title}
                      </h2>
                    </div>

                    <span className="w-fit rounded-md bg-slate-50 px-3 py-1.5 text-xs font-bold uppercase text-gray-500 ring-1 ring-slate-200">
                      {section.buttons.length} practice types
                    </span>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {section.buttons.map((button) => (
                      <button
                        key={button.path}
                        type="button"
                        onClick={() => handleClick(button.path)}
                        className={`${section.color} ${section.hover} flex min-h-[44px] w-full items-center justify-center gap-2 rounded-sm px-4 py-2 text-center text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-purple-100`}
                      >
                        <Cpu size={18} aria-hidden="true" />
                        <span className="min-w-0 truncate">{button.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
